import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { AXNUILayoutHelpTextInner } from "@iddataweb/idw-common-ui/types";
import { ThemingGenericContentSlotValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { TextareaFormGroup } from "../../../../../../common/textarea-form-group/textarea-form-group";
import { GenericContentSlotHtmlAndCssVariablesContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/common/generic-content-slot-content-type-container/generic-content-slot-html-and-css-variables-container/generic-content-slot-html-and-css-variables-container";
import { getHtmlTextVariables } from "@utils/custom-html-and-css-util";
import styles from "./content-slot-custom-html-and-css-container.less";

type ContentSlotCustomHtmlAndCssContainerProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    customHtml: string;
    defaultHtml: string;
    customCss: string;
    defaultCss: string;
    validationErrors: ThemingGenericContentSlotValidationErrors;
    htmlTextVariables: Record<string, string>;
    updateFormContentSlotContentField: (contentField: Partial<AXNUILayoutHelpTextInner>, htmlTextVariables?: Record<string, string>) => void;
    updateHtmlTextVariables: (htmlTextVariables: Record<string, string>) => void;
};

export const ContentSlotCustomHtmlAndCssContainer = (props: ContentSlotCustomHtmlAndCssContainerProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        customHtml,
        defaultHtml,
        customCss,
        defaultCss,
        validationErrors,
        htmlTextVariables,
        updateFormContentSlotContentField,
        updateHtmlTextVariables
    } = props;

    const handleCustomHtmlChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { hasChanges, variables } = getHtmlTextVariables(htmlTextVariables, event.target.value);
        updateFormContentSlotContentField({ htmlText: event.target.value }, hasChanges ? variables : null);
    };

    const handleResetCustomHtml = () => {
        updateFormContentSlotContentField({ htmlText: defaultHtml });
    };

    const handleCustomCssChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateFormContentSlotContentField({ cssContent: event.target.value });
    };

    const handleResetCustomCss = () => {
        updateFormContentSlotContentField({ cssContent: defaultCss });
    };

    return (
        <div>
            <TextareaFormGroup
                className={styles.expandedTextArea}
                controlId={"content-slot-custom-html-input"}
                automationId={"content-slot-custom-html-input"}
                label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.customHtml.label}
                ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.customHtml.label}
                layout={layout}
                labelWidth={labelWidth}
                onChange={handleCustomHtmlChange}
                value={customHtml || ""}
                disabled={isDisabled}
                isRequired={true}
                afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.customHtml.tooltip("pre form help text")}
                afterLabelIcon={isDisabled ? null : faUndo}
                afterLabelIconOnClick={handleResetCustomHtml}
            />
            {validationErrors.customHtmlError}
            <GenericContentSlotHtmlAndCssVariablesContainer
                isDisabled={isDisabled}
                labelWidth={labelWidth}
                htmlTextVariables={htmlTextVariables}
                updateHtmlTextVariables={updateHtmlTextVariables}
            />
            <TextareaFormGroup
                controlId={"content-slot-custom-css-input"}
                automationId={"content-slot-custom-css-input"}
                label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.customCss.label}
                ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.customCss.label}
                layout={layout}
                labelWidth={labelWidth}
                onChange={handleCustomCssChange}
                value={customCss || ""}
                disabled={isDisabled}
                isRequired={true}
                afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.customCss.tooltip("pre form help text")}
                afterLabelIcon={isDisabled ? null : faUndo}
                afterLabelIconOnClick={handleResetCustomCss}
            />
            {validationErrors.customCssError}
        </div>
    );
};